export const ATTRIBUTE_FILTERS = [
    { key: 'city', label: 'City' },
    { key: 'province', label: 'Province' },
    { key: 'skills', label: 'Skills', listValue: true },
    { key: 'occupation', label: 'Occupation' },
    { key: 'industry', label: 'Industry' },
    { key: 'education_level', label: 'Education level' },
    { key: 'job_title', label: 'Job title' },
    { key: 'job_level', label: 'Job level' },
];

const FILTER_KEYS = ATTRIBUTE_FILTERS.map(filter => filter.key);
const LIST_KEYS = ATTRIBUTE_FILTERS.filter(filter => filter.listValue).map(filter => filter.key);

const clean = value => String(value ?? '').trim();
const fold = value => clean(value).toLowerCase();

/** A user's values for one attribute; list attributes such as skills are comma separated. */
export const userAttributeValues = (user, key) => {
    const raw = user?.[key] ?? user?.profile?.[key];
    if (Array.isArray(raw)) return raw.map(clean).filter(Boolean);
    const text = clean(raw);
    if (!text) return [];
    return LIST_KEYS.includes(key) ? text.split(',').map(clean).filter(Boolean) : [text];
};

export const usersHaveAttributes = users => (users || []).some(user => FILTER_KEYS.some(key => userAttributeValues(user, key).length));

/** `{ key: [{ value, count }] }` from the facets endpoint, keeping only known attributes with values. */
export const normaliseFacets = (raw) => {
    const source = raw?.data || raw || {};
    const facets = {};
    FILTER_KEYS.forEach((key) => {
        const rows = Array.isArray(source[key]) ? source[key] : [];
        const values = rows
            .map(row => (typeof row === 'string' ? { value: row, count: null } : { value: clean(row?.value), count: Number.isFinite(Number(row?.count)) ? Number(row.count) : null }))
            .filter(row => row.value);
        if (values.length) facets[key] = values;
    });
    return facets;
};

/** Facets worked out from the loaded users when the API does not provide them. */
export const facetsFromUsers = (users) => {
    const facets = {};
    FILTER_KEYS.forEach((key) => {
        const counts = new Map();
        (users || []).forEach((user) => {
            new Set(userAttributeValues(user, key).map(fold)).forEach((folded) => {
                const label = userAttributeValues(user, key).find(value => fold(value) === folded);
                const entry = counts.get(folded) || { value: label, count: 0 };
                entry.count += 1;
                counts.set(folded, entry);
            });
        });
        const values = [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
        if (values.length) facets[key] = values;
    });
    return facets;
};

export const hasActiveFilters = filters => Object.values(filters || {}).some(values => values?.length);

/** Every attribute with selections must match; values within one attribute are alternatives. */
export const userMatchesFilters = (user, filters) => Object.entries(filters || {}).every(([key, selected]) => {
    if (!selected?.length) return true;
    const values = userAttributeValues(user, key).map(fold);
    return selected.some(value => values.includes(fold(value)));
});

export const filtersToParams = (filters) => {
    const params = {};
    Object.entries(filters || {}).forEach(([key, selected]) => {
        if (FILTER_KEYS.includes(key) && selected?.length) params[key] = selected.join(',');
    });
    return params;
};
